// v2/components/CotizacionResumen.jsx
//
// Muestra el detalle de una cotización vigente: items con su precio
// individual, descuentos aplicados, total y countdown hasta expiración.

import React, { useEffect, useState } from 'react';
import { Card, Typography, Tag, Divider, Space, Alert, Statistic } from 'antd';
import { ClockCircleOutlined, GiftOutlined, DollarOutlined } from '@ant-design/icons';

const { Text } = Typography;

export default function CotizacionResumen({ cotizacion, expirada }) {
  const [restante, setRestante] = useState(0);

  useEffect(() => {
    if (!cotizacion?.expiraEn) return undefined;
    const tick = () => {
      const ms = new Date(cotizacion.expiraEn).getTime() - Date.now();
      setRestante(Math.max(0, ms));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cotizacion]);

  if (!cotizacion) return null;

  const minutos = Math.floor(restante / 60000);
  const segundos = Math.floor((restante % 60000) / 1000);
  const tagColor = restante < 60_000 ? 'red' : restante < 180_000 ? 'orange' : 'green';

  return (
    <Card
      title={<><DollarOutlined /> Resumen de tu compra</>}
      extra={
        expirada
          ? <Tag color="red" icon={<ClockCircleOutlined />}>Expirada</Tag>
          : <Tag color={tagColor} icon={<ClockCircleOutlined />}>
              Válida {String(minutos).padStart(2, '0')}:{String(segundos).padStart(2, '0')}
            </Tag>
      }
    >
      {expirada && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning" showIcon
          message="La cotización expiró. Refresca el carrito para volver a cotizar."
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {(cotizacion.items || []).map((it, idx) => (
          <ItemFila key={idx} item={it} />
        ))}

        <Divider style={{ margin: '8px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary">Subtotal</Text>
          <Text strong>${(cotizacion.total || 0).toLocaleString('es-CL')}</Text>
        </div>

        <DescuentosTotales cotizacion={cotizacion} />

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 16,
          background: '#e6f4ff',
          borderRadius: 8
        }}>
          <Text strong style={{ fontSize: 16 }}>Total a pagar</Text>
          <Statistic
            value={cotizacion.total || 0}
            valueStyle={{ color: '#0958d9', fontWeight: 700 }}
            formatter={v => `$${Number(v).toLocaleString('es-CL')}`}
          />
        </div>
      </Space>
    </Card>
  );
}

function ItemFila({ item }) {
  const { descuentos = {}, precioFinal, plan, tipoPrecio, tipo, inscrito } = item;
  const tieneDescVecino = descuentos.descuentoVecino > 0 && descuentos.esVecino;

  return (
    <div style={{ padding: 12, background: '#fafafa', borderRadius: 6 }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text strong>
            {tipo === 'matricula'
              ? `Matrícula${item.tipoVigencia === 'indefinida' ? ' · de por vida' : item.tipoVigencia === 'anual' ? ` · ${item.aniosVigencia > 1 ? `${item.aniosVigencia} años` : '1 año'}` : ''}`
              : (item.tallerNombre || 'Taller')}
          </Text>
          <Text strong style={{ color: '#0958d9' }}>
            ${(precioFinal || 0).toLocaleString('es-CL')}
          </Text>
        </div>
        {inscrito?.nombre && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {inscrito.nombre} {inscrito.rut ? `(${inscrito.rut})` : ''} {plan ? `· Plan ${plan}` : ''}
          </Text>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tieneDescVecino && (
            <Tag color="blue" icon={<GiftOutlined />}>
              Tarjeta Vecino — ahorras ${descuentos.descuentoVecino.toLocaleString('es-CL')}
            </Tag>
          )}
          {tipoPrecio === 'adultoMayor' && (
            <Tag color="blue">Tarifa Adulto Mayor</Tag>
          )}
          {tipoPrecio === 'funcionario' && (
            <Tag color="blue">Tarifa Funcionario</Tag>
          )}
          {tipo === 'matricula' && item.agregadaAutomaticamente && (
            <Tag color="gold">Se suma porque no tiene matrícula vigente</Tag>
          )}
          {tipoPrecio === 'particular' && precioFinal === 0 && (
            <Tag>Gratuito</Tag>
          )}
        </div>
      </Space>
    </div>
  );
}

function DescuentosTotales({ cotizacion }) {
  const total = (cotizacion.items || []).reduce(
    (s, i) => s + (i.descuentos?.totalDescuento || 0),
    0
  );
  if (total <= 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Text type="secondary">Descuentos aplicados</Text>
      <Text strong style={{ color: '#0047bb' }}>
        −${total.toLocaleString('es-CL')}
      </Text>
    </div>
  );
}
